import React, { useState, useRef, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import SearchPanel from './SearchPanel';
import { mapNight, mapDay } from 'config/configureMap';
import { geocodeCoords, getCityID } from 'utils/mapFunctions';
import { handleError } from 'reducers/ErrorReducer';

const useStyles = makeStyles((theme) => ({
  map: {
    height: 200,
  },
  marker: {
    position: 'sticky',
    minWidth: 20,
    height: 20,
    borderRadius: '50%',
    background: theme.palette.secondary.main,
    color: '#fff',
    textAlign: 'center',
    paddingTop: 3,
    boxSizing: 'border-box',
    border: '1px solid #000',
    cursor: 'pointer',
  },
  activeMarker: {
    position: 'sticky',
    minWidth: 22,
    height: 22,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    color: '#fff',
    textAlign: 'center',
    paddingTop: 4,
    boxSizing: 'border-box',
    border: '1px solid #000',
    cursor: 'pointer',
    transform: 'translate(-1px, -1px)',
  },
  addresses: {
    height: 75,
    paddingTop: 8,
    overflowY: 'auto',
    overflowX: 'hidden',
    '& > div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      padding: theme.spacing(0.25, 2),
      cursor: 'pointer',
    },
  },
  rebidAddress: {
    padding: theme.spacing(0.25, 2),
    cursor: 'pointer',
    background: theme.palette.secondary.main,
    color: theme.colors.secondaryContrast,
    marginTop: -8,
  },
}));

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const positionOverlayView = (width, height) => ({
  x: -width / 2,
  y: -height / 2,
});

const mapOptions = {
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
};

function RebidsMapPenel(props) {
  const classes = useStyles();
  const { CallerPhone, e911, updateCoords, themeMode, setLocation } = props;
  const [selected, setSelected] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [rebidAddress, setRebidAddress] = useState(null);
  const rebids = e911[CallerPhone];
  const mountedRef = useRef(true);
  const mapRef = useRef();
  const zoomRef = useRef();
  mapOptions.styles = themeMode === 'day' ? mapDay : mapNight;
  const data = rebids?.data || [];

  useEffect(() => {
    if (!data.length) return;
    updateE911Record();
    // eslint-disable-next-line
  }, [e911]);

  useEffect(() => {
    zoomRef.current = 14;
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    mapRef.current.setZoom(zoomRef.current);
  }, []);

  const updateE911Record = () => {
    const record = data[data.length - 1];
    const lat = parseFloat(record.YCoordinate);
    const lng = parseFloat(record.XCoordinate);
    if (lat && lng) setMapCenter({ lat, lng });
    const address = getAddressFromRebid(record);
    if (address.StreetName) setRebidAddress(address);
  };

  const getAddressFromRebid = (rebid) => {
    let Notes = '';
    if (rebid.BldgIdentifier) Notes = `BldgIdentifier: ${rebid.BldgIdentifier}`;
    if (rebid.AddressSuffix) {
      if (Notes) Notes += ', ';
      Notes += `AddressSuffix: ${rebid.AddressSuffix}`;
    }

    const address = {
      AddressNumber: rebid.AddressNumber,
      County: null,
      Notes: Notes ? Notes : null,
      PostDirection: null,
      PostalCode: '70806',
      PreDirection: null,
      State: 'LA',
      StreetName: rebid.AddressStreet,
      StreetType: null,
      formattedAddress: getFullAddressTextFromRebid(rebid),
      placeName: 'East Baton Rouge Parish',
      ptsCityID: rebid.Community ? getCityID(rebid.Community) : null,
      XCoordinate: rebid.XCoordinate ? rebid.XCoordinate : null,
      YCoordinate: rebid.YCoordinate ? rebid.YCoordinate : null,
    };
    return address;
  };

  const getFullAddressTextFromRebid = (rebid) => {
    const AddressNumber = rebid.AddressNumber !== null ? rebid.AddressNumber : '';
    const StreenName = rebid.AddressStreet ? ` ${rebid.AddressStreet}` : '';
    const City = rebid.Community ? `, ${rebid.Community}` : '';
    const State = rebid.State ? `, ${rebid.State}` : '';
    const PostalCode = rebid.PostalCode ? `, ${rebid.PostalCode}` : '';
    const UnitNumber = rebid.AddressUnitNumber !== null ? ` Unit ${rebid.AddressUnitNumber}` : '';
    return AddressNumber + StreenName + UnitNumber + City + State + PostalCode;
  };

  const MyMarker = (props) => {
    const { no } = props;
    return (
      <OverlayView
        {...props}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={positionOverlayView}>
        <div
          className={selected === no ? classes.activeMarker : classes.marker}
          onClick={props.onClick}>
          {no}
        </div>
      </OverlayView>
    );
  };

  const handleMarkerClick = (rebid) => {
    const uniqueAddresses = [];
    const { XCoordinate, YCoordinate } = rebid;
    const coords = { lat: parseFloat(YCoordinate), lng: parseFloat(XCoordinate) };
    updateCoords(rebid);
    setSelected(rebid.no);
    geocodeCoords(coords)
      .then((results) => {
        if (!mountedRef.current) return;
        const addresses = results.filter((address) => {
          const { formattedAddress } = address;
          if (uniqueAddresses.indexOf(formattedAddress) !== -1) return false;
          uniqueAddresses.push(formattedAddress);
          return true;
        });
        setAddresses(addresses);
        if (addresses.length) setLocation({ ...addresses[0], IsPrimary: true });
      })
      .catch((err) => {
        props.handleError(err, 'Error, Problem with geocoding service');
      });
  };

  const handleDefaultAddressClick = () => {
    updateCoords(rebidAddress);
    setLocation({ ...rebidAddress, IsPrimary: true });
  };

  const renderRebid = (rebid) => {
    const { ptsE911ID, XCoordinate, YCoordinate, no } = rebid;
    const lat = parseFloat(YCoordinate);
    const lng = parseFloat(XCoordinate);
    if (lat === NaN || lng === NaN) return '';

    return (
      <MyMarker
        key={ptsE911ID}
        position={{ lat, lng }}
        no={no}
        onClick={() => handleMarkerClick(rebid)}
      />
    );
  };

  const renderRebids = () => {
    const rebids = data
      .map((rebid, idx) => {
        return { ...rebid, no: idx + 1 };
      })
      .filter((rebid) => !isNaN(rebid.YCoordinate));
    return rebids.map(renderRebid);
  };

  const renderMap = () => {
    return (
      <div className={classes.map}>
        {mapCenter !== null && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={8}
            center={mapCenter}
            options={mapOptions}
            key={themeMode}
            onLoad={onMapLoad}
            className={classes.map}>
            {renderRebids()}
          </GoogleMap>
        )}
      </div>
    );
  };

  const renderAddresses = () => {
    return (
      <div className={classes.addresses}>
        {rebidAddress !== null && (
          <div className={classes.rebidAddress} onClick={handleDefaultAddressClick}>
            {rebidAddress.formattedAddress}
          </div>
        )}
        {addresses.map((address) => (
          <div
            key={address.formattedAddress}
            onClick={() => setLocation({ ...address, IsPrimary: true })}>
            {address.formattedAddress}
          </div>
        ))}
      </div>
    );
  };

  return (
    <SearchPanel title="Rebids Map">
      {renderMap()}
      {renderAddresses()}
    </SearchPanel>
  );
}

const mapStateToProps = (state) => {
  return {
    e911: state.e911,
    themeMode: state.theme.mode,
    mapOptions: state.map.options,
  };
};

export default connect(mapStateToProps, { handleError })(RebidsMapPenel);
